
import { namespace } from 'vuex-class';
import { Vue, Component, Prop, Ref } from 'vue-property-decorator';

import { CollectionType } from '@/enums/collection-type';
import type { FilterCollection } from '@/stores/filter.store';

import { getArticleBackdrop } from '@/helpers/editorial-helper';
import { ListProvision } from '@/helpers/composables/useArticle';
import { EditorialListTitleFragment } from '@/pages/graphql/fragments/EditorialListTitle.fragment';

import ListArticleItem from '@/components/editorial/ListArticleItem.vue';
import ListOffersComparison from '@/components/editorial/ListOffersComparison.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import GuideList from '@/components/editorial/GuideList.vue';
import { WebLocale } from '@/enums/web-locale';
const TimelineEmptyState = () =>
	import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue');
const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');

const Filter = namespace('filter');
const Language = namespace('language');

@Component({
	name: 'ListArticle',
	components: {
		ListArticleItem,
		FilterBarSeo,
		ListOffersComparison,
		TimelineEmptyState,
		InfiniteScroll,
		GuideList,
	},
	metaInfo() {
		return this.head;
	},
})
export default class ListArticle extends Vue {
	@Prop({ required: true }) article: ListProvision['article'];
	@Prop({ required: true }) mainObject: ListProvision['mainObject'];

	@Filter.Getter(CollectionType.EDITORIAL) filters: FilterCollection;

	@Language.Getter country: string;
	@Language.State webLocale: WebLocale;

	@Ref('infinite-scroll') infiniteScroll: InfiniteScroll;

	readMore = false;
	collectionType = CollectionType;

	get size() {
		return this.mainObject.titles.totalCount;
	}

	get titles(): EditorialListTitleFragment[] {
		return (this.mainObject.titles.edges ?? []).map(({ node }) => node);
	}

	get isEmpty() {
		return this.titles.length === 0 || this.size === 0;
	}

	get isAtEndOfList() {
		return !this.mainObject.titles.pageInfo.hasNextPage;
	}

	get isLoading() {
		return this.$apollo.loading;
	}

	get head() {
		const backdropUrl = getArticleBackdrop(this.mainObject, this.$store.state, 'backdrop');
		return {
			meta: [
				{ vmid: 'og:image', property: 'og:image', content: backdropUrl },
				{ vmid: 'og:image:width', property: 'og:image:width', content: '1920' },
				{ vmid: 'og:image:height', property: 'og:image:height', content: '900' },
			],
		};
	}

	/* TRACKING */
	trackClick(e: Event) {
		const target = e.target as HTMLElement;
		if (target.nodeName !== 'A') return;

		this.$emit('content-clicked', e);
	}
}
